import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFileUrl } from '../utils/fileUtils';
import API from '../utils/api';
import { toast } from 'react-toastify';
import ApplyModal from '../components/ApplyModal';
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaRupeeSign,
  FaClock,
  FaBookmark,
  FaRegBookmark,
  FaShareAlt,
  FaTimes,
  FaCheckCircle,
  FaWhatsapp,
  FaLinkedin,
  FaTwitter,
  FaEye,
} from 'react-icons/fa';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [relatedJobs, setRelatedJobs] = useState([]);
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    fetchJob();
    trackView();
    // Check if apply action is in URL
    if (searchParams.get('action') === 'apply') {
      if (!isAuthenticated) {
        toast.info('Please login to apply for this job');
        navigate('/login', { state: { from: `/jobs/${id}?action=apply` } });
      } else {
        setShowApplyModal(true);
      }
    }
  }, [id, searchParams, isAuthenticated]);

  const trackView = async () => {
    try {
      // Check if this job was viewed in the last 24 hours
      const viewedJobsKey = 'viewedJobs';
      const viewedJobs = JSON.parse(localStorage.getItem(viewedJobsKey) || '{}');
      const now = Date.now();
      const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours

      // Check if job was recently viewed
      if (viewedJobs[id] && (now - viewedJobs[id]) < oneDayInMs) {
        // Already viewed within 24 hours, don't track again
        return;
      }

      // Track the view
      await API.post(`/jobs/${id}/view`);
      
      // Update localStorage with current timestamp
      viewedJobs[id] = now;
      
      // Clean up old entries (older than 30 days)
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
      Object.keys(viewedJobs).forEach(jobId => {
        if (now - viewedJobs[jobId] > thirtyDaysInMs) {
          delete viewedJobs[jobId];
        }
      });
      
      localStorage.setItem(viewedJobsKey, JSON.stringify(viewedJobs));
    } catch (error) {
      // Silent fail - view tracking shouldn't affect UX
      console.error('Failed to track view:', error);
    }
  };

  const fetchJob = async () => {
    try {
      const { data } = await API.get(`/jobs/${id}`);
      setJob(data);
      // fetch related jobs by same category (domain-specific)
      if (data?.category) {
        fetchRelated(data.category, data._id);
      }
      // Check if job is saved and if user has applied
      if (isAuthenticated && user?.role === 'jobseeker') {
        try {
          const { data: userData } = await API.get('/users/profile');
          setIsSaved(userData.savedJobs?.some(savedJob => 
            (typeof savedJob === 'string' ? savedJob : savedJob._id) === id
          ));
          
          // Check if user has already applied
          const { data: applicationsData } = await API.get('/applications/my');
          console.log('My Applications:', applicationsData);
          const alreadyApplied = applicationsData.applications?.some(app => {
            const jobId = typeof app.job === 'string' ? app.job : app.job?._id;
            console.log('Comparing:', jobId, 'with', id);
            return jobId === id;
          });
          console.log('Has Applied:', alreadyApplied);
          setHasApplied(alreadyApplied);
        } catch (error) {
          console.error('Failed to check saved status:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Job not found');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelated = async (category, currentJobId) => {
    try {
      const { data } = await API.get('/jobs', { params: { category, limit: 12 } });
      // Ensure returned jobs match the requested category (backend should filter, but double-check on frontend)
      const jobs = (data.jobs || [])
        .filter(j => j.category === category)
        .filter(j => j._id !== currentJobId)
        .slice(0, 5);
      setRelatedJobs(jobs);
    } catch (err) {
      console.error('Failed to fetch related jobs:', err);
    }
  };

  const handleSaveJob = async () => {
    if (saving) return;
    
    try {
      setSaving(true);
      const { data } = await API.post(`/users/save-job/${id}`);
      setIsSaved(data.saved);
      toast.success(data.message);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to save jobs');
      } else {
        toast.error('Failed to save job');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleShare = (platform) => {
    const jobUrl = window.location.href;
    const jobTitle = encodeURIComponent(job.title);
    const companyName = encodeURIComponent(job.company?.name || 'Unknown Company');
    const text = encodeURIComponent(`Check out this job: ${job.title} at ${job.company?.name || 'Unknown Company'}`);
    
    let shareUrl = '';
    
    switch(platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${text}%20${encodeURIComponent(jobUrl)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(jobUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareModal(false);
    toast.success('Opening share dialog...');
  };

  const copyJobLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Job link copied to clipboard!');
    setShowShareModal(false);
  };

  const handleApplySuccess = () => {
    // Update hasApplied state after successful application
    setHasApplied(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!job) return null;

  const formatSalary = () => {
    return `₹${job.salary.min / 100000}–${job.salary.max / 100000} LPA`;
  };

  const formatExperience = () => {
    return `${job.experience.min}–${job.experience.max} years`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] pt-16 pb-32 overflow-hidden">
        {/* Abstract Background Elements */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-white opacity-10 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 rounded-full bg-white opacity-10 blur-2xl"></div>
        
        <div className="container mx-auto px-4 relative z-10 flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-2xl p-2 shadow-2xl flex items-center justify-center overflow-hidden shrink-0">
            {job.company?.logo ? (
              <img
                src={getFileUrl(`uploads/${job.company.logo}`)}
                alt={job.company.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <span className={`text-4xl font-bold text-[#00c2ab] ${job.company?.logo ? 'hidden' : 'flex'}`}>
              {job.company?.name?.charAt(0)}
            </span>
          </div>
          
          <div className="text-center md:text-left flex-1">
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-2 tracking-tight">{job.title}</h1>
            <p className="text-lg md:text-2xl text-teal-50 font-medium opacity-90">{job.company?.name}</p>
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={handleSaveJob}
              disabled={saving}
              className={`p-4 rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1 ${
                isSaved ? 'bg-white text-[#00c2ab]' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-md'
              }`}
              title={isSaved ? 'Remove from saved jobs' : 'Save this job'}
            >
              {isSaved ? <FaBookmark className="text-xl" /> : <FaRegBookmark className="text-xl" />}
            </button>
            <button 
              onClick={() => setShowShareModal(true)}
              className="p-4 bg-white/20 hover:bg-white/30 backdrop-blur-md text-white rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              title="Share this job"
            >
              <FaShareAlt className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-16 relative z-20 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8 animate-fadeIn">
            {/* Job Description */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                  <FaBriefcase className="text-sm" />
                </span>
                Job Description
              </h2>
              <div className="prose max-w-none text-gray-700 whitespace-pre-line leading-relaxed">
                {job.description}
              </div>
            </div>

            {/* Skills Required */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-extrabold text-gray-900 mb-6 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                  <FaCheckCircle className="text-sm" />
                </span>
                Skills Required
              </h2>
              <div className="flex flex-wrap gap-3">
                {job.skills.map((skill, index) => (
                  <span key={index} className="px-4 py-2 bg-teal-50 text-[#00c2ab] font-bold rounded-xl text-sm border border-teal-100 shadow-sm">
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Responsibilities</h2>
                <ul className="space-y-3">
                  {job.responsibilities.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <FaCheckCircle className="text-[#00c2ab] mt-1 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Requirements</h2>
                <ul className="space-y-3">
                  {job.requirements.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-700">
                      <FaCheckCircle className="text-[#00c2ab] mt-1 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Company Info */}
            {job.company && (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <h2 className="text-2xl font-extrabold text-gray-900 mb-6">About {job.company.name}</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">{job.company.description}</p>
                {job.company.website && (
                  <a
                    href={job.company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-xl font-bold transition-colors shadow-sm"
                  >
                    Visit Website
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Sticky Sidebar */}
          <div className="lg:col-span-1 animate-fadeIn">
            <div className="sticky top-24 space-y-6">
              {/* Job Highlights Card */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                
                {/* Apply Button */}
                <div className="mb-8">
                  {user?.role !== 'employer' && (
                    hasApplied ? (
                      <button
                        disabled
                        className="w-full py-4 bg-green-50 text-green-600 font-bold rounded-xl flex items-center justify-center gap-2 cursor-not-allowed border border-green-200"
                      >
                        <FaCheckCircle className="text-xl" />
                        Application Submitted
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            toast.info('Please login to apply for this job');
                            navigate('/login', { state: { from: `/jobs/${id}?action=apply` } });
                          } else {
                            setShowApplyModal(true);
                          }
                        }}
                        className="w-full py-4 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl font-bold text-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300"
                      >
                        Apply Now
                      </button>
                    )
                  )}
                </div>

                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                      <FaRupeeSign className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Salary</p>
                      <p className="font-extrabold text-gray-900">{formatSalary()}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                      <FaBriefcase className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Experience</p>
                      <p className="font-extrabold text-gray-900">{formatExperience()}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                      <FaMapMarkerAlt className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Location</p>
                      <p className="font-extrabold text-gray-900">{job.location.city}, {job.location.state}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                      <FaClock className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Posted On</p>
                      <p className="font-extrabold text-gray-900">{new Date(job.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-[#00c2ab]">
                      <FaEye className="text-xl" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Views</p>
                      <p className="font-extrabold text-gray-900">{job.views || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 font-bold rounded-lg text-sm shadow-sm">{job.workMode}</span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 font-bold rounded-lg text-sm shadow-sm">{job.employmentType}</span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 font-bold rounded-lg text-sm shadow-sm">{job.category}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Jobs */}
        {relatedJobs && relatedJobs.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-8">Similar Jobs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedJobs.map(rj => (
                <div key={rj._id} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 truncate"><a href={`/jobs/${rj._id}`} className="hover:text-[#00c2ab]">{rj.title}</a></h3>
                  <p className="text-gray-500 mb-4">{rj.company?.name}</p>
                  <div className="flex justify-between items-center text-sm font-semibold text-gray-400">
                    <span className="flex items-center gap-1"><FaMapMarkerAlt /> {rj.location?.city}</span>
                    <span className="flex items-center gap-1"><FaBriefcase /> {rj.employmentType}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 relative animate-fadeIn">
            <button
              onClick={() => setShowShareModal(false)}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-900 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            >
              <FaTimes size={16} />
            </button>

            <h2 className="text-2xl font-extrabold text-gray-900 mb-8 text-center">Share Job</h2>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <button onClick={() => handleShare('whatsapp')} className="flex flex-col items-center gap-2 group">
                <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition-colors">
                  <FaWhatsapp className="text-3xl text-green-500 group-hover:scale-110 transition-transform" />
                </div>
                <span className="text-xs font-bold text-gray-600">WhatsApp</span>
              </button>

              <button onClick={() => handleShare('linkedin')} className="flex flex-col items-center gap-2 group">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <FaLinkedin className="text-3xl text-blue-600 group-hover:scale-110 transition-transform" />
                </div>
                <span className="text-xs font-bold text-gray-600">LinkedIn</span>
              </button>

              <button onClick={() => handleShare('twitter')} className="flex flex-col items-center gap-2 group">
                <div className="w-14 h-14 rounded-2xl bg-sky-50 flex items-center justify-center group-hover:bg-sky-100 transition-colors">
                  <FaTwitter className="text-3xl text-sky-500 group-hover:scale-110 transition-transform" />
                </div>
                <span className="text-xs font-bold text-gray-600">Twitter</span>
              </button>
            </div>

            <button
              onClick={copyJobLink}
              className="w-full py-4 bg-gray-50 hover:bg-gray-100 text-gray-800 rounded-xl font-bold transition-colors border border-gray-200"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      <ApplyModal 
        job={job} 
        isOpen={showApplyModal} 
        onClose={() => setShowApplyModal(false)}
        onApplySuccess={handleApplySuccess}
      />
    </div>
  );
};

export default JobDetails;
